import { formatTableValue } from "./format-table-value"
import {
  MY_BRAND_DRAWER_PERFORMANCE_CATALOG,
  PERFORMANCE_EMPTY_SLOT_COUNT,
  PERFORMANCE_GRID_SLOT_COUNT,
  type PerformanceKpiCatalogEntry,
} from "./my-brand-drawer-performance-catalog"

export type PerformanceTrendTone = "positive" | "negative" | "flat" | null

export interface PerformanceKpiCellModel {
  kind: "kpi"
  id: string
  label: string
  value: string
  trendLabel: string | null
  trendTone: PerformanceTrendTone
}

export interface PerformanceEmptyCellModel {
  kind: "empty"
}

export type PerformanceGridSlotModel = PerformanceKpiCellModel | PerformanceEmptyCellModel

/** Retool precision: offsets at 1 decimal, shares at 0 decimals. */
const OFFSET_DECIMALS = 1
const SHARE_DECIMALS = 0

/** Above this magnitude a pill percent is abbreviated (1k, 100k, 1m, …). */
const PILL_ABBREVIATE_THRESHOLD = 1_000
const PILL_UNITS = [
  { limit: 1_000_000_000, suffix: "b" },
  { limit: 1_000_000, suffix: "m" },
  { limit: 1_000, suffix: "k" },
] as const

function extractNumber(row: Record<string, unknown>, key: string): number | null {
  const raw = row[key]
  if (raw === null || raw === undefined) return null
  const n = typeof raw === "number" ? raw : Number(raw)
  return Number.isFinite(n) ? n : null
}

function roundTo(value: number, decimals: number): number {
  const p = Math.pow(10, decimals)
  return Math.round(value * p) / p
}

/**
 * Render a pill percent, abbreviating magnitudes of 1,000+ so the badge stays
 * compact — `1500` → "1.5k", `100000` → "100k", `1000000` → "1m". The sign is
 * preserved; the caller still adds the leading "+" for positive offsets.
 */
function formatPillPercent(value: number): string {
  const abs = Math.abs(value)
  if (abs < PILL_ABBREVIATE_THRESHOLD) return `${value}`

  const sign = value < 0 ? "-" : ""
  const unit = PILL_UNITS.find(({ limit }) => abs >= limit) ?? PILL_UNITS.at(-1)!
  return `${sign}${roundTo(abs / unit.limit, 1)}${unit.suffix}`
}

function offsetTone(
  rounded: number,
  betterDirection: PerformanceKpiCatalogEntry["betterDirection"],
): PerformanceTrendTone {
  if (rounded === 0) return "flat"
  const favorable = betterDirection === "low" ? rounded < 0 : rounded > 0
  return favorable ? "positive" : "negative"
}

/**
 * The peer-comparison badge for one KPI. Shared with the user-catalog-driven
 * drawer grid so both render identical arithmetic (Retool parity lives here).
 */
export function performanceTrend(
  entry: Pick<PerformanceKpiCatalogEntry, "comparison" | "betterDirection">,
  current: number | null,
  baseline: number | null,
): { trendLabel: string | null; trendTone: PerformanceTrendTone } {
  if (entry.comparison === "none") return { trendLabel: null, trendTone: null }
  if (current === null || baseline === null || baseline === 0) {
    return { trendLabel: null, trendTone: null }
  }

  if (entry.comparison === "offset") {
    const rounded = roundTo(((current - baseline) / baseline) * 100, OFFSET_DECIMALS)
    return {
      trendLabel: `${rounded > 0 ? "+" : ""}${formatPillPercent(rounded)}%`,
      trendTone: offsetTone(rounded, entry.betterDirection),
    }
  }

  // share: entity's portion of the peer-population total.
  const rounded = roundTo((current / baseline) * 100, SHARE_DECIMALS)
  return { trendLabel: `${formatPillPercent(rounded)}%`, trendTone: "flat" }
}

/** Reads `current_<suffix>` / `baseline_<suffix>` off a drawer performance row. */
export function performanceRowValues(
  row: Record<string, unknown>,
  sqlSuffix: string,
): { current: number | null; baseline: number | null } {
  return {
    current: extractNumber(row, `current_${sqlSuffix}`),
    baseline: extractNumber(row, `baseline_${sqlSuffix}`),
  }
}

function buildKpiCell(
  entry: PerformanceKpiCatalogEntry,
  row: Record<string, unknown>,
  emptyValue: string,
  label: string,
): PerformanceKpiCellModel {
  const { current, baseline } = performanceRowValues(row, entry.sqlSuffix)
  const value =
    current === null ? emptyValue : formatTableValue(current, entry.format)
  const { trendLabel, trendTone } = performanceTrend(entry, current, baseline)

  return {
    kind: "kpi",
    id: entry.id,
    label,
    value,
    trendLabel,
    trendTone,
  }
}

export function mapMyBrandDrawerPerformanceModel(
  row: Record<string, unknown> | null | undefined,
  labelsById: Record<string, string>,
  emptyValue: string,
): PerformanceGridSlotModel[] {
  const kpiCells = MY_BRAND_DRAWER_PERFORMANCE_CATALOG.map((entry) =>
    buildKpiCell(
      entry,
      row ?? {},
      emptyValue,
      labelsById[entry.id] ?? entry.defaultLabel,
    ),
  )

  const emptySlots: PerformanceEmptyCellModel[] = Array.from(
    { length: PERFORMANCE_EMPTY_SLOT_COUNT },
    () => ({ kind: "empty" }),
  )

  const slots = [...kpiCells, ...emptySlots]
  if (slots.length !== PERFORMANCE_GRID_SLOT_COUNT) {
    throw new Error(`Performance grid must emit ${PERFORMANCE_GRID_SLOT_COUNT} slots`)
  }
  return slots
}
